/**
 * Landing → prospector source_key (central: ai-act-landing-page-source-key-infra-v3).
 * Five fields: four optional MATERIAL + target_product as routing/ownership.
 */

export const LANDING_SOURCE_KEY_STORAGE = 'krysalis_landing_source_key';

/** Canonical structured source key carried into prospect-agent sessions. */
export interface LandingSourceKey {
  conversation_starter?: string;
  target_icp?: string;
  market_fit_rubric?: string;
  target_product?: string;
  referral_partner?: string;
  /**
   * Company prospect code for the prospect agent (e.g. `RC` = Resilience Circle).
   * The agent matches the person; this does not pre-assign an ICP.
   */
  prospect_code?: string;
  /** Legacy rainbow-magnet aliases (ai-imp-rainbow-lead-magnets-wp009). */
  source_magnet?: string;
  source_channel?: string;
  source_copy_variant?: string;
}

const CANONICAL_FIELDS = [
  'conversation_starter',
  'target_icp',
  'market_fit_rubric',
  'target_product',
  'referral_partner',
  'prospect_code',
] as const;

const LEGACY_FIELDS = ['source_magnet', 'source_channel', 'source_copy_variant'] as const;

/** Query / path aliases accepted from reference URLs. */
const FIELD_ALIASES: Record<(typeof CANONICAL_FIELDS)[number] | (typeof LEGACY_FIELDS)[number], string[]> = {
  conversation_starter: ['conversation_starter', 'starter', 'opening'],
  target_icp: ['target_icp', 'icp'],
  market_fit_rubric: ['market_fit_rubric', 'marketfit_rubric', 'rubric'],
  target_product: ['target_product', 'product'],
  referral_partner: ['referral_partner', 'partner', 'ref'],
  prospect_code: ['prospect_code', 'code'],
  source_magnet: ['source_magnet', 'magnet'],
  source_channel: ['source_channel', 'channel'],
  source_copy_variant: ['source_copy_variant', 'copy_variant', 'variant'],
};

function pickParam(params: URLSearchParams, aliases: string[]): string | undefined {
  for (const alias of aliases) {
    const raw = params.get(alias);
    if (raw != null && raw.trim()) return raw.trim();
  }
  return undefined;
}

/** Parse source_key fields from a URLSearchParams (or location.search). */
export function parseLandingSourceKeyFromSearch(
  search: string | URLSearchParams
): LandingSourceKey | null {
  const params =
    typeof search === 'string'
      ? new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
      : search;

  const out: LandingSourceKey = {};
  let any = false;

  for (const field of [...CANONICAL_FIELDS, ...LEGACY_FIELDS]) {
    const value = pickParam(params, FIELD_ALIASES[field]);
    if (value) {
      out[field] = value;
      any = true;
    }
  }

  // Nested JSON blob: ?source_key={"target_product":"..."}
  const blob = params.get('source_key') ?? params.get('sk');
  if (blob) {
    try {
      const parsed = JSON.parse(blob) as Record<string, unknown>;
      if (parsed && typeof parsed === 'object') {
        for (const field of [...CANONICAL_FIELDS, ...LEGACY_FIELDS]) {
          const v = parsed[field];
          if (typeof v === 'string' && v.trim() && !out[field]) {
            out[field] = v.trim();
            any = true;
          }
        }
      }
    } catch {
      /* ignore malformed blob */
    }
  }

  return any ? out : null;
}

export function isEmptyLandingSourceKey(key: LandingSourceKey | null | undefined): boolean {
  if (!key) return true;
  return ![...CANONICAL_FIELDS, ...LEGACY_FIELDS].some((f) => Boolean(key[f]?.trim()));
}

/** Merge incoming into existing; incoming non-empty fields win. */
export function mergeLandingSourceKeys(
  base: LandingSourceKey | null | undefined,
  incoming: LandingSourceKey | null | undefined
): LandingSourceKey | null {
  if (isEmptyLandingSourceKey(incoming)) {
    return isEmptyLandingSourceKey(base) ? null : { ...(base as LandingSourceKey) };
  }
  return { ...(base ?? {}), ...(incoming as LandingSourceKey) };
}

export function readPersistedLandingSourceKey(): LandingSourceKey | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(LANDING_SOURCE_KEY_STORAGE);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LandingSourceKey;
    return isEmptyLandingSourceKey(parsed) ? null : parsed;
  } catch {
    return null;
  }
}

export function persistLandingSourceKey(key: LandingSourceKey | null): void {
  if (typeof sessionStorage === 'undefined') return;
  if (isEmptyLandingSourceKey(key)) {
    sessionStorage.removeItem(LANDING_SOURCE_KEY_STORAGE);
    return;
  }
  sessionStorage.setItem(LANDING_SOURCE_KEY_STORAGE, JSON.stringify(key));
}

/**
 * Capture from current location.search, merge with any stored key, persist.
 * Returns the merged key (or null).
 */
export function captureLandingSourceKeyFromLocation(
  search: string = typeof window !== 'undefined' ? window.location.search : ''
): LandingSourceKey | null {
  const fromUrl = parseLandingSourceKeyFromSearch(search);
  const merged = mergeLandingSourceKeys(readPersistedLandingSourceKey(), fromUrl);
  persistLandingSourceKey(merged);
  return merged;
}

/** Flatten for Krybot bootstrap / thread metadata (snake_case string map). */
export function landingSourceKeyToMetadata(
  key: LandingSourceKey | null | undefined
): Record<string, string> {
  if (isEmptyLandingSourceKey(key) || !key) return {};
  const out: Record<string, string> = {};
  for (const field of [...CANONICAL_FIELDS, ...LEGACY_FIELDS]) {
    const v = key[field]?.trim();
    if (v) out[field] = v;
  }
  out.source_key_json = JSON.stringify(
    Object.fromEntries(
      CANONICAL_FIELDS.filter((f) => key[f]?.trim()).map((f) => [f, key[f]!.trim()])
    )
  );
  return out;
}

/** Build register/login next URL that preserves source_key query params. */
export function buildProspectorEntryPath(key?: LandingSourceKey | null): string {
  const resolved = key ?? readPersistedLandingSourceKey();
  const meta = landingSourceKeyToMetadata(resolved);
  const params = new URLSearchParams();
  for (const field of CANONICAL_FIELDS) {
    if (meta[field]) params.set(field, meta[field]);
  }
  for (const field of LEGACY_FIELDS) {
    if (meta[field]) params.set(field, meta[field]);
  }
  const qs = params.toString();
  return qs ? `/onboarding/prospector?${qs}` : '/onboarding/prospector';
}
